import { Injectable } from 'angular-ts-decorators';

import { Initable } from './initable';
import { interval, SchedulerLike } from 'rxjs';
import { switchMapTo, map, filter, take } from 'rxjs/operators';
import { CurrentSession } from './current-session';
import { IIntercomAppId } from './intercom-app-id';

/**
 * The intercom provider encapsulates the Intercom live chat
 * service.
 *
 * # Setup
 *
 * To use this service, the Intercom Javascript library must be
 * loaded on the index.html page.
 * https://docs.intercom.com/install-on-your-product-or-site/other-ways-to-get-started/integrate-intercom-in-a-single-page-app
 */
@Injectable('intercom')
export class Intercom implements Initable {
  disabled: boolean;

  constructor(
    private $q: ng.IQService,
    private $window: ng.IWindowService,
    private intercomAppId: IIntercomAppId,
    private scheduler: SchedulerLike,
    private session,
  ) {
    'ngInject';
    this.disabled = false;
  }

  /**
   *
   * This method initializes Intercom. The appId should be set before calling
   * this method.
   *
   * @returns {Void} returns nothing
   */
  init(): ng.IPromise<any> {
    if (this.disabled) {
      return this.$q.when();
    }

    if (!this.intercomAppId) {
      // appId is required to boot up
      return this.$q.when();
    }

    // stream that emits once when Intercom is loaded
    const intercomAvailable$ = interval(100, this.scheduler).pipe(
      map(() => angular.isDefined(this.$window.Intercom)),
      filter((value) => value === true),
      take(1),
    );

    intercomAvailable$.subscribe(() => {
      if (!this.$window.Intercom) {
        // eslint-disable-next-line no-console
        console.log('boot too early, the Intercom is missing from $window');
      }

      // eslint-disable-next-line new-cap
      this.$window.Intercom('boot', {
        app_id: this.intercomAppId,
      });
    });

    intercomAvailable$
      .pipe(switchMapTo(this.session.current$))
      .subscribe((currentSession: CurrentSession) => {
        this.update({
          name: currentSession.username,
          email: currentSession.email,
          user_id: currentSession.userId,
        });
      });

    return this.$q.when();
  }

  /**
   *
   * This method can update intercom any time the route changes for example.
   * See documentation https://developers.intercom.com/docs/intercom-javascript
   * @param {object} options an optional object with options.
   *
   * @returns {Void} returns nothing
   */
  // TODO hide this method, it's not used oustide of the service & it's tests
  update(options: object): void {
    if (!this.$window.Intercom) {
      // eslint-disable-next-line no-console
      console.log('update too early, the Intercom is missing from $window');
      return;
    }
    // eslint-disable-next-line new-cap
    this.$window.Intercom('update', options);
  }

  /**
   *
   * This method can show the Intercom chat bubble
   * See documentation https://developers.intercom.com/docs/intercom-javascript
   *
   * @returns {Void} returns nothing
   */
  show(): void {
    if (this.disabled) {
      // eslint-disable-next-line no-console
      console.log('show intercom is disabled');
      return;
    }

    if (!this.$window.Intercom) {
      // eslint-disable-next-line no-console
      console.log('show too early, the Intercom is missing from $window');
      return;
    }
    // eslint-disable-next-line new-cap
    this.$window.Intercom('show');
  }

  /**
   *
   * This method can hide the Intercom chat bubble
   *
   * @returns {Void} returns nothing
   */
  hide(): void {
    if (!this.$window.Intercom) {
      // eslint-disable-next-line no-console
      console.log('hide too early, the Intercom is missing from $window');
      return;
    }
    // eslint-disable-next-line new-cap
    this.$window.Intercom('hide');
  }

  /**
   *
   * This method disables the Intercom chat bubble permanently
   *
   * @returns {Void} returns nothing
   */
  disable(): void {
    this.disabled = true;
    if (!this.$window.Intercom) {
      // eslint-disable-next-line no-console
      console.log('disable too early, the Intercom is missing from $window');
      return;
    }
    // eslint-disable-next-line new-cap
    this.$window.Intercom('hide');
  }
}
